import { useEffect, useState } from 'react';
import { AlertCircle, Timer } from 'lucide-react';
import { Button, Modal, Textarea, toast } from '../../components/ui';
import { formatHours } from '../../lib/format';
import {
  useStopTimer,
  useTimesheetAction,
  type PeriodKind,
  type RunningTimer,
  type TimesheetAction,
} from './api';
import { completenessWarning, type WeekCompleteness } from './completeness';
import { addDaysIso, formatHoursUnit, weekRangeLabel, type HoursFormat } from './hours';

/**
 * The confirmation step for the four transitions that deserve one.
 *
 * `unsubmit` deliberately has no dialog: withdrawing your own submission is
 * instant and reversible, and a modal in front of it would be ceremony. The
 * other four either change what an approver has signed off or send work back
 * to a human, so each gets a sentence of context and a place to say why.
 */

export type DialogAction = Extract<TimesheetAction, 'submit' | 'approve' | 'reject' | 'reopen'>;

interface Copy {
  title: string;
  confirm: string;
  /** Prompt above the note field. */
  notePrompt: string;
  notePlaceholder: string;
  /** A rejection with no reason is a period bounced back to somebody guessing. */
  noteRequired: boolean;
  destructive?: boolean;
  /** Sentence explaining the consequence, shown above the note. */
  consequence: string;
}

/** The one thing a dialog needs to know about the period it acts on. */
export interface DialogPeriod {
  id: string | null;
  periodKind: PeriodKind;
  periodStart: string;
  periodEnd: string;
  label: string;
}

const KIND_WORD: Record<PeriodKind, string> = { DAY: 'day', WEEK: 'week', MONTH: 'month' };

/**
 * The copy, per period. A monthly contractor submits a MONTH and a daily one a
 * DAY — "Submit this week" over August's hours would describe the wrong thing.
 */
function copyFor(action: DialogAction, period: DialogPeriod): Copy {
  const word = KIND_WORD[period.periodKind];
  const noun = period.periodKind === 'WEEK' ? 'this week' : period.label;
  switch (action) {
    case 'submit':
      return {
        title: `Submit ${noun} for approval`,
        confirm:
          period.periodKind === 'WEEK'
            ? 'Submit week'
            : `Submit ${period.label.replace(/, \d{4}$/, '')}`,
        notePrompt: 'Note for your approver (optional)',
        notePlaceholder:
          period.periodKind === 'MONTH' ? 'Two weeks off mid-month' : 'Short week — Thursday was PTO',
        noteRequired: false,
        consequence: `The ${word} becomes read-only while it waits. You can withdraw it yourself any time before it is reviewed.`,
      };
    case 'approve':
      return {
        title: `Approve ${noun}`,
        confirm: `Approve ${word}`,
        notePrompt: 'Note (optional)',
        notePlaceholder: 'Looks right — thanks',
        noteRequired: false,
        consequence: `The ${word} stays read-only until someone reopens it. Approving does not bill anything — invoicing is separate.`,
      };
    case 'reject':
      return {
        title: `Send ${noun} back`,
        confirm: 'Send back',
        notePrompt: 'What needs to change?',
        notePlaceholder:
          'Tuesday has 11 h on Meridian with no description — please split and describe it.',
        noteRequired: true,
        destructive: true,
        consequence: `The ${word} reopens for editing and this note is the first thing its owner sees.`,
      };
    case 'reopen':
    default:
      return {
        title: `Reopen ${noun}`,
        confirm: `Reopen ${word}`,
        notePrompt: 'Why is it being reopened? (optional)',
        notePlaceholder: 'Missing Friday — please add it and resubmit',
        noteRequired: false,
        consequence: `The approval is withdrawn and the ${word} becomes editable again. Your name and this note are shown to its owner.`,
      };
  }
}

export interface TimesheetActionDialogProps {
  /** null closes the dialog. */
  action: DialogAction | null;
  /** The period the transition is about (a day, a week or a month). */
  period?: DialogPeriod | null;
  /** @deprecated — pass `period`. Read as a WEEK starting on this Monday. */
  weekStart?: string;
  personId?: string;
  personName: string;
  /** Hours in the week — the figure being signed off. */
  hours?: number;
  /**
   * What the app already knows about whether this week is finished.
   *
   * ⚠ It is shown, never enforced. Jesse's constraint is *fewer steps*, so
   * there is no extra confirm, no checkbox and no disabled button — a short
   * week is often a correct week (PTO, a part-timer, a Wednesday start). The
   * failure this closes is the app printing "27.00 STILL TO LOG THIS WEEK" in
   * amber six inches below a Submit dialog that said nothing at all.
   */
  completeness?: WeekCompleteness;
  /** So the dialog's hours read in the same unit as the grid behind it. */
  hoursFormat?: HoursFormat;
  /**
   * THE CLOCK THE SERVER IS ABOUT TO REFUSE THIS SUBMISSION FOR.
   *
   * `TimesheetService.submit` 409s while a timer runs in the week, and the app
   * knew about that timer the whole time - it is in the topbar. The dialog
   * used to open with its primary button live and no mention of it: you
   * pressed Submit, got a red ring around the NOTE field (the wrong control)
   * reading "A timer is still running in this week", then had to close the
   * dialog, find the timer, stop it and start again. Four steps to be told
   * something the screen already knew. Now the dialog says so before the
   * press, and the one button does both.
   */
  runningTimer?: RunningTimer | null;
  /**
   * Nothing is logged in the period. The server refuses an empty submission
   * without a note (a month off is a real answer, but it has to be said), so
   * the dialog asks for one up front instead of letting the press fail.
   */
  empty?: boolean;
  onClose: () => void;
  /** Fired after a successful transition (close a menu, refocus, …). */
  onDone?: () => void;
}

export function TimesheetActionDialog({
  action,
  period: periodProp,
  weekStart,
  personId,
  personName,
  hours,
  completeness,
  hoursFormat = 'decimal',
  runningTimer = null,
  empty = false,
  onClose,
  onDone,
}: TimesheetActionDialogProps) {
  const period: DialogPeriod | null =
    periodProp ??
    (weekStart
      ? {
          id: null,
          periodKind: 'WEEK',
          periodStart: weekStart,
          periodEnd: addDaysIso(weekStart, 6),
          label: weekRangeLabel(weekStart),
        }
      : null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  // Hooks cannot be conditional, so the mutation is created for a safe default
  // when the dialog is closed and never fires in that state.
  const mutation = useTimesheetAction(action ?? 'submit');
  const stopTimer = useStopTimer();

  useEffect(() => {
    if (action) {
      setNote('');
      setError(null);
      setNoteError(null);
    }
  }, [action]);

  if (!action || !period) return null;
  const base = copyFor(action, period);
  /** An empty period needs its reason said — the server requires it. */
  const needsNote = base.noteRequired || (action === 'submit' && empty);
  const copy: Copy =
    action === 'submit' && empty
      ? {
          ...base,
          notePrompt: 'Nothing is logged — say why (required)',
          notePlaceholder: 'On leave the whole month',
          noteRequired: true,
        }
      : base;
  /**
   * The gap, said at the moment of commitment. Only on the two transitions that
   * put a week beyond its owner's reach — withdrawing and reopening make it
   * editable again, so a completeness note there would be noise.
   */
  const gap =
    completeness && (action === 'submit' || action === 'approve')
      ? completenessWarning(completeness, hoursFormat)
      : null;

  /**
   * A clock running INSIDE the week being submitted. Its hours would land in a
   * week that is already read-only the moment it stops, which is why the
   * server refuses - so the refusal is stated here instead of being discovered.
   */
  const blockingTimer =
    action === 'submit' &&
    runningTimer &&
    runningTimer.date >= period.periodStart &&
    runningTimer.date <= period.periodEnd
      ? runningTimer
      : null;

  const run = async () => {
    const trimmed = note.trim();
    if (needsNote && !trimmed) {
      setNoteError(
        action === 'submit'
          ? 'Add a note — an empty period is only accepted with a reason.'
          : 'Add a note — it is the only thing telling them what to fix.',
      );
      return;
    }
    try {
      // ONE PRESS DOES BOTH. Stopping first is not a second decision: the week
      // cannot be submitted with a clock in it, and the button says so.
      if (blockingTimer) {
        const stopped = await stopTimer.mutateAsync({ personId });
        toast.success(
          `Timer stopped — ${formatHours(stopped.elapsedHours, { unit: true })} added to ${
            blockingTimer.agreement.name
          }.`,
        );
      }
      await mutation.mutateAsync({
        ...(period.id
          ? { timesheetId: period.id }
          : { personId, periodKind: period.periodKind, periodStart: period.periodStart }),
        note: trimmed ? trimmed : null,
      });
      const what = period.periodKind === 'WEEK' ? `the week of ${period.label}` : period.label;
      toast.success(
        action === 'submit'
          ? `${capitalise(what)} submitted for approval.`
          : action === 'approve'
            ? `${personName}: ${what} approved.`
            : action === 'reject'
              ? `Sent back to ${personName} with your note.`
              : `${personName}: ${what} reopened for editing.`,
      );
      onClose();
      onDone?.();
    } catch (err) {
      // Kept IN the dialog rather than thrown at a toast: the reason a
      // submission was refused ("a timer is still running in this week") is
      // usually something to fix before trying again, so it has to stay
      // readable next to the button that failed.
      setError(err instanceof Error ? err.message : 'That did not work — try again.');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={copy.title}
      description={
        <>
          {personName} · {period.label}
          {hours !== undefined && (
            <>
              {' · '}
              {/* The grid's own unit — a dialog reading "13 h" over a sheet
                  reading 13:00 is two formats for one number on one screen. */}
              <span className="num font-semibold text-secondary">
                {formatHoursUnit(hours, hoursFormat)}
              </span>
            </>
          )}
        </>
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={mutation.isPending || stopTimer.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={run}
            loading={mutation.isPending || stopTimer.isPending}
            className={copy.destructive ? 'bg-primary hover:bg-primary-600' : undefined}
          >
            {blockingTimer ? 'Stop timer and submit' : copy.confirm}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/* The timer the server would have refused this submission for, named
            BEFORE the press rather than discovered after it. */}
        {blockingTimer && (
          <div className="flex items-start gap-2 rounded-lg border border-accent/40 bg-accent/[0.07] px-3 py-2">
            <Timer className="mt-px h-4 w-4 shrink-0 text-accent" aria-hidden />
            <p className="text-body-sm leading-snug text-secondary">
              <span className="font-bold">A timer is still running</span> on{' '}
              <span className="font-semibold">{blockingTimer.agreement.name}</span>
              {blockingTimer.task ? ` · ${blockingTimer.task.name}` : ''}.
              <span className="mt-0.5 block font-normal text-grey-600">
                A {KIND_WORD[period.periodKind]} cannot be submitted with a clock in it — the
                elapsed time would land where it is already read-only. This one press stops it
                and submits.
              </span>
            </p>
          </div>
        )}
        {/* The refusal itself, in a block of its own. It used to be handed to
            the note Textarea, so a server error about a TIMER drew a red ring
            around "Note for your approver" — the wrong control entirely. */}
        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-3 py-2"
          >
            <AlertCircle className="mt-px h-4 w-4 shrink-0 text-error" aria-hidden />
            <p className="text-body-sm leading-snug text-error">{error}</p>
          </div>
        )}
        {/* What the app already knew and used to keep to itself. Informational,
            never blocking: a short week is often a correct week. */}
        {gap && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
            <AlertCircle className="mt-px h-4 w-4 shrink-0 text-status-partial" aria-hidden />
            <p className="text-body-sm leading-snug text-status-partial">
              <span className="font-bold">
                {action === 'submit' ? 'This week looks unfinished' : 'Approving an unfinished week'}
              </span>{' '}
              — <span className="num">{gap}</span>.
              <span className="mt-0.5 block font-normal text-grey-600">
                {action === 'submit'
                  ? 'Fine if that is the week you worked (PTO, a short contract). Close this if you meant to fill it in.'
                  : 'Fine if you know why — the note below is where to say so.'}
              </span>
            </p>
          </div>
        )}
        <p className="text-body-sm text-grey-500">{copy.consequence}</p>
        <Textarea
          label={copy.notePrompt}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (noteError) setNoteError(null);
            if (error) setError(null);
          }}
          rows={3}
          autoFocus
          placeholder={copy.notePlaceholder}
          textareaClassName="resize-none"
          error={noteError ?? undefined}
        />
      </div>
    </Modal>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
